<script setup>
import { ref, onMounted, getCurrentInstance } from 'vue';
import { platform } from '@core/platform';

const { proxy } = getCurrentInstance();
const { $bus, $constants, $stores } = proxy;

const periodOptions = [
  { value: 'today', label: "Aujourd'hui" },
  { value: 'week', label: 'Cette semaine' },
  { value: 'month', label: 'Ce mois' },
  { value: 'year', label: 'Cette année' }
];

const selectedPeriod = ref('month');

onMounted(() => {
  $bus.emit($constants.EVENTS.HOME.STATS.REFRESH);
});

const stats = $stores.home.state.stats;
</script>

<template>
  <PageLayout>
    <template #headerTitle>Dashboard</template>

    <div class="px-4 sm:px-6 lg:px-8 mt-4 sm:mt-6 space-y-4 sm:space-y-6">
      <!-- FIRST ROW - Client Balance & Total Transactions -->
      <div
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
      >
        <!-- Client Balance (Kingrecharge Only) -->
        <div
          v-if="platform.serviceName === 'kingrecharges'"
          class="rounded-2xl sm:rounded-3xl p-4 sm:p-6"
          style="background-color: var(--bg-card)"
        >
          <div class="flex items-center justify-between gap-3">
            <div class="flex-1 min-w-0">
              <p class="text-xs sm:text-sm text-gray-500 font-medium">
                Solde des clients
              </p>
              <p
                class="text-xl sm:text-2xl md:text-3xl font-bold mt-1 sm:mt-2 truncate"
              >
                {{ stats.data.clientBalance?.toLocaleString() || '...' }} CFA
              </p>
              <button
                @click="$bus.emit('WALLET.WITHDRAW_MODAL.SHOW')"
                class="mt-3 px-4 py-1.5 bg-primary text-white text-xs font-bold rounded-lg hover:opacity-90 transition-all flex items-center gap-2"
              >
                <span>Retirer</span>
                <ColorImg src="/images/all/money-1.svg" class="w-3 h-3 bg-white" />
              </button>
            </div>
            <div
              class="w-12 h-12 sm:w-14 sm:h-14 text-primary-500 bg-primary-100 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0"
            >
              <span class="w-full h-full p-3">
                <ColorImg
                  src="/images/all/money-1.svg"
                  alt=""
                  class="w-full h-full bg-current/70"
                />
              </span>
            </div>
          </div>
        </div>

        <!-- Total Transactions -->
        <div class="rounded-2xl sm:rounded-3xl p-4 sm:p-6" style="background-color: var(--bg-card)">
          <div class="flex items-center justify-between gap-3">
            <div class="flex-1 min-w-0">
              <p class="text-xs sm:text-sm text-gray-500 font-medium">
                Total transactions
              </p>
              <p
                class="text-xl sm:text-2xl md:text-3xl font-bold mt-1 sm:mt-2 truncate"
              >
                {{ stats.data.totalTransactions?.toLocaleString() || '...' }}
              </p>
            </div>
            <div
              class="w-12 h-12 sm:w-14 sm:h-14 text-blue-500 bg-blue-100 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0"
            >
              <span class="w-full h-full p-3">
                <ColorImg
                  src="/images/all/stats.svg"
                  alt=""
                  class="w-full h-full bg-current/70"
                />
              </span>
            </div>
          </div>
        </div>
        <!-- Total Transactions -->
        <div class="rounded-2xl sm:rounded-3xl p-4 sm:p-6" style="background-color: var(--bg-card)">
          <div class="flex items-center justify-between gap-3">
            <div class="flex-1 min-w-0">
              <p class="text-xs sm:text-sm text-gray-500 font-medium">
                Total Clients
              </p>
              <p
                class="text-xl sm:text-2xl md:text-3xl font-bold mt-1 sm:mt-2 truncate"
              >
                {{ stats.data.totalCustomers?.toLocaleString() || '...' }}
              </p>
            </div>
            <div
              class="w-12 h-12 sm:w-14 sm:h-14 text-blue-500 bg-blue-100 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0"
            >
              <span class="w-full h-full p-3">
                <ColorImg
                  src="/images/all/user.svg"
                  alt=""
                  class="w-full h-full bg-current/70"
                />
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- SECOND ROW - Net Profit, Successful, Failed with Period Selector -->
      <div
        v-if="true"
        class="rounded-2xl sm:rounded-3xl p-4 sm:p-6" style="background-color: var(--bg-card)"
      >
        <!-- Period Selector -->
        <div
          class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6"
        >
          <h3 class="text-base sm:text-lg font-semibold">
            Statistiques de transactions
          </h3>
          <div class="flex flex-wrap gap-1.5 sm:gap-2 w-full sm:w-auto">
            <button
              v-for="option in periodOptions"
              :key="option.value"
              @click="selectedPeriod = option.value"
              :class="[
                'px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all text-center',
                selectedPeriod === option.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50 -sm'
              ]"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <!-- Stats Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
          <!-- Net Profit -->
          <div class="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-5">
            <div class="flex items-center gap-2 sm:gap-3">
              <div
                class="w-10 h-10 sm:w-12 sm:h-12 text-green-500 bg-green-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
              >
                <span class="w-full h-full p-3">
                  <ColorImg
                    src="/images/all/stats.svg"
                    alt=""
                    class="w-full h-full bg-current/70"
                  />
                </span>
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-xs sm:text-sm text-gray-500">Profit net</p>
                <p
                  class="text-lg sm:text-xl md:text-2xl font-bold text-green-600 truncate"
                >
                  {{ stats.data.netProfit?.toLocaleString() || '...' }} CFA
                </p>
              </div>
            </div>
          </div>

          <!-- Successful Transactions -->
          <div class="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-5">
            <div class="flex items-center gap-2 sm:gap-3">
              <div
                class="w-10 h-10 sm:w-12 sm:h-12 text-emerald-500 bg-emerald-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
              >
                <span class="w-full h-full p-3">
                  <ColorImg
                    src="/images/all/check.svg"
                    alt=""
                    class="w-full h-full bg-current/70"
                  />
                </span>
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-xs sm:text-sm text-gray-500">Réussies</p>
                <p
                  class="text-lg sm:text-xl md:text-2xl font-bold text-emerald-600 truncate"
                >
                  {{ stats.data.successfulTransactions?.toLocaleString() || '...' }}
                </p>
              </div>
            </div>
          </div>

          <!-- Failed Transactions -->
          <div class="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-5">
            <div class="flex items-center gap-2 sm:gap-3">
              <div
                class="w-10 h-10 sm:w-12 sm:h-12 text-red-500 bg-red-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
              >
                <span class="w-full h-full p-3">
                  <ColorImg
                    src="/images/all/close.svg"
                    alt=""
                    class="w-full h-full bg-current/70"
                  />
                </span>
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-xs sm:text-sm text-gray-500">Échouées</p>
                <p
                  class="text-lg sm:text-xl md:text-2xl font-bold text-red-600 truncate"
                >
                  {{ stats.data.failedTransactions?.toLocaleString() || '...' }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- LATEST TRANSACTIONS -->
      <div class="rounded-2xl sm:rounded-3xl p-3 sm:p-4" style="background-color: var(--bg-card)">
        <div class="flex justify-between px-3 sm:px-4">
          <div class="font-bold text-sm sm:text-base">
            Transactions récentes
          </div>
          <router-link to="/history" class="text-sm sm:text-base"
            >Tout voir</router-link
          >
        </div>
        <div class="mt-3 sm:mt-4">
          <LatestTransactions />
        </div>
      </div>
    </div>
  </PageLayout>
</template>
